import imageCompression from 'browser-image-compression'
import { SUPPORTED_IMAGE_EXTENSIONS, SUPPORTED_IMAGE_MIME_TYPES } from '@/config/imageProcessing'
import { MAX_ORIGINAL_IMAGE_SIZE_MB } from '@/config/imageSize'
import { ProcessingError, type ImageProcessingConfig, type ProcessedImageResult } from '@/types/imageProcessing'

function getExtension(fileName: string): string {
  const idx = fileName.lastIndexOf('.')
  return idx === -1 ? '' : fileName.slice(idx).toLowerCase()
}

export function validateOriginalImageSize(file: File, config: ImageProcessingConfig): void {
  const maxOriginalSizeMB = config.maxOriginalSizeMB ?? MAX_ORIGINAL_IMAGE_SIZE_MB
  const maxBytes = maxOriginalSizeMB * 1024 * 1024
  if (file.size > maxBytes) {
    throw new ProcessingError('size-exceeds-absolute-max', `File exceeds maximum size of ${maxOriginalSizeMB} MB`)
  }
}

export function validateImageDimensions(width: number, height: number, config: ImageProcessingConfig): void {
  if (!config.maxPixels) return
  const totalPixels = width * height
  if (totalPixels > config.maxPixels) {
    throw new ProcessingError('dimensions-exceed-max', `Image resolution (${width}x${height}) exceeds maximum allowed pixels`)
  }
}

export function validateImageFile(file: File): void {
  if (file.size === 0) {
    throw new ProcessingError('corrupt', 'File is empty')
  }
  const ext = getExtension(file.name)
  const mimeOk = !file.type || SUPPORTED_IMAGE_MIME_TYPES.has(file.type)
  if (!SUPPORTED_IMAGE_EXTENSIONS.has(ext) || !mimeOk) {
    throw new ProcessingError('invalid-type', 'Unsupported file type. Use JPG or PNG.')
  }
}

export function probeDecodable(file: Blob): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new ProcessingError('corrupt', 'File appears corrupted or is not a valid image.'))
    }
    img.src = url
  })
}

export function verifyProcessedImage(
  result: { size: number; width: number; height: number },
  config: ImageProcessingConfig
): boolean {
  const maxBytes = config.maxSizeMB * 1024 * 1024
  if (result.size > maxBytes) return false
  const largestDim = Math.max(result.width, result.height)
  if (largestDim > config.maxWidthOrHeight + 1) return false
  if (largestDim < config.minWidthOrHeight) return false
  return true
}

export async function compressWithFallback(
  file: File,
  config: ImageProcessingConfig,
  signal?: AbortSignal
): Promise<ProcessedImageResult> {
  const originalSizeBytes = file.size
  let quality = config.initialQuality
  let maxDim = config.maxWidthOrHeight
  let attempt = 0
  const startTime = Date.now()
  const timeoutMs = config.compressionTimeoutMs ?? 30_000

  while (attempt < config.maxRetries) {
    if (signal?.aborted) {
      throw new ProcessingError('aborted', 'Processing cancelled')
    }

    const elapsedMs = Date.now() - startTime
    if (elapsedMs > timeoutMs) {
      throw new ProcessingError('compression-timeout', 'Compression exceeded maximum duration')
    }

    const compressed = await imageCompression(file, {
      maxSizeMB: config.maxSizeMB,
      maxWidthOrHeight: maxDim,
      initialQuality: quality,
      useWebWorker: true,
      signal,
    })

    const { width, height } = await probeDecodable(compressed)

    if (verifyProcessedImage({ size: compressed.size, width, height }, config)) {
      return {
        file: new File([compressed], file.name, { type: compressed.type || file.type }),
        wasCompressed: true,
        originalSizeBytes,
        finalSizeBytes: compressed.size,
        width,
        height,
      }
    }

    attempt += 1
    if (quality > config.minQuality) {
      quality = Math.max(config.minQuality, quality - config.qualityStep)
    } else if (maxDim * config.dimensionStep >= config.minWidthOrHeight) {
      maxDim = Math.round(maxDim * config.dimensionStep)
    } else {
      throw new ProcessingError(
        'dimension-floor-hit',
        'Reached the minimum usable resolution before the image could be brought within the size limit.'
      )
    }
  }

  throw new ProcessingError(
    'compression-failed',
    'Unable to compress image within the configured size and dimension limits.'
  )
}